import {
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
  inputLabelClasses,
  switchClasses
} from "@mui/material";
import React from "react";
import CustomTooltip from "@hyro-components/Tooltip";
import QuestionIcon from "src/icons/insights/QuestionIcon";
import ServiceGroup from "./ServiceGroup";
import {
  WIZARD_MODES,
  getServicesForUseCase,
  getExtraFieldsForUseCase
} from "../DeployWizard.consts";
import { WizardContainer } from "../DeployWizard.styles";

const StepConfiguration = ({
  mode,
  selectedUseCase,
  formData,
  latestToggles,
  errors,
  onFieldChange,
  onValueChange,
  onLatestToggle
}) => {
  const { required, optional } = getServicesForUseCase(selectedUseCase);
  const extraFields = getExtraFieldsForUseCase(selectedUseCase);

  const renderExtraField = (field) => {
    if (field.inputType === "switch") {
      return (
        <Stack key={field.id} gap={0.5}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                sx={{ ml: 1, [`& .${switchClasses.thumb}`]: { boxShadow: (theme) => theme.shadows[4] } }}
                name={field.id}
                checked={!!formData[field.id]}
                onChange={(e) => onFieldChange(field.id, e.target.checked)}
              />
            }
            label={
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle2">{field.label}</Typography>
                {field.tooltip && (
                  <CustomTooltip title={field.tooltip}>
                    <Stack alignItems="center">
                      <QuestionIcon />
                    </Stack>
                  </CustomTooltip>
                )}
              </Stack>
            }
          />
          {field.subtitle && (
            <Typography variant="caption" color="text.600" sx={{ ml: 6 }}>
              {field.subtitle}
            </Typography>
          )}
        </Stack>
      );
    }

    return (
      <TextField
        key={field.id}
        size="small"
        name={field.id}
        label={field.label}
        value={formData[field.id] || ""}
        onChange={(e) => onFieldChange(field.id, e.target.value)}
        error={!!errors[field.id]}
        helperText={errors[field.id] || ""}
        fullWidth
      />
    );
  };

  return (
    <WizardContainer>
      <Typography variant="h6" mb={2}>
        Configure your environment
      </Typography>

      <Stack gap={3}>
        <Stack direction="row" gap={2}>
          <TextField
            size="small"
            name="releaseName"
            label="Release Name"
            value={formData.releaseName || ""}
            onChange={(e) => onFieldChange("releaseName", e.target.value)}
            error={!!errors.releaseName}
            helperText={errors.releaseName || ""}
            disabled={mode === WIZARD_MODES.EDIT}
            fullWidth
            required
            sx={{ [`& .${inputLabelClasses.asterisk}`]: { color: "error.main" } }}
          />
          <TextField
            size="small"
            name="ttlHours"
            label="TTL (hours)"
            value={formData.ttlHours || ""}
            onChange={(e) => onFieldChange("ttlHours", e.target.value)}
            error={!!errors.ttlHours}
            helperText={errors.ttlHours || ""}
            type="number"
            sx={{ width: 160 }}
          />
        </Stack>

        <ServiceGroup
          title="Required Services"
          services={required}
          formData={formData}
          latestToggles={latestToggles}
          errors={errors}
          onFieldChange={onFieldChange}
          onValueChange={onValueChange}
          onLatestToggle={onLatestToggle}
        />

        {optional.length > 0 && (
          <ServiceGroup
            title="Optional Services (defaults to dev)"
            services={optional}
            formData={formData}
            latestToggles={latestToggles}
            errors={errors}
            onFieldChange={onFieldChange}
            onValueChange={onValueChange}
            onLatestToggle={onLatestToggle}
          />
        )}

        {extraFields.length > 0 && (
          <Stack gap={2}>
            <Typography variant="subtitle2">
              Additional Settings
            </Typography>
            {extraFields.map(renderExtraField)}
          </Stack>
        )}
      </Stack>
    </WizardContainer>
  );
};

export default StepConfiguration;
